/** Field history for a record, from the audit log: who changed what, from what, to what. */
import type { AuditEntry } from "@tlcrm/shared";

import { useAudit } from "../api/hooks";
import { when } from "../lib/format";
import { EmptyState, ErrorState } from "./ui";

const LABELS: Record<string, string> = {
  first_name: "First name",
  last_name: "Last name",
  lifecycle_stage: "Stage",
  owner_id: "Owner",
  date_of_birth: "Date of birth",
  do_not_call: "Do not call",
  do_not_email: "Do not email",
  do_not_text: "Do not text",
  household_id: "Household",
  household_role: "Household role",
  postal_code: "ZIP",
  tobacco_use: "Tobacco",
  height_inches: "Height (in)",
  weight_pounds: "Weight (lb)",
  disqualify_reason: "Disqualify reason",
  carrier_id: "Carrier Sold",
  product_id: "Policy Type",
  customer_id: "Customer",
  date_sold: "Date Sold",
  total_premium: "Total Premium",
  total_coverage: "Total Coverage",
  effective_date: "Policy Effective Date",
  insured_dob: "DOB",
  post_sale_status: "Post-Sale Status",
  cancellation_date: "Cancellation Date",
  cancellation_reason: "Cancellation Reason",
  agent_comp: "Agent's Comp",
  expected_commission: "Expected Commission",
  commission_rule_id: "Commission rule",
  commission_status: "Commission status",
  commission_paid: "Commission Paid",
  commission_amount: "Commission Amount",
  deducted_amount: "Deducted Amount",
  rewrite_date: "Date of Rewrite",
  former_carrier: "Former Carrier",
  call_type: "Call Type",
  call_outcome: "Outcome of The Call",
  calling_for: "Calling For",
  pitch_used: "Pitch Used",
  current_policy_type: "Current Policy Type",
  loss_reason: "Loss Reason",
  business_type: "Type",
};

const label = (key: string) =>
  LABELS[key] ?? key.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

function show(v: unknown): string {
  if (v === null || v === undefined || v === "") return "empty";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function Entry({ e }: { e: AuditEntry }) {
  const who = e.actor?.fullName ?? (e.client ? `${e.client} (integration)` : "System");
  const changes =
    e.action === "update" ? Object.entries(e.changes as Record<string, [unknown, unknown]>) : [];
  return (
    <li className="tl-item">
      <span className="tl-icon mono" style={{ fontSize: "var(--fs-micro)" }}>
        {e.action === "create" ? "+" : e.action === "delete" ? "−" : "Δ"}
      </span>
      <div>
        <div className="tl-head">
          <b>
            {who}{" "}
            {e.action === "create"
              ? "created this record"
              : e.action === "delete"
                ? "deleted this record"
                : e.action === "restore"
                  ? "restored this record"
                  : "changed"}
          </b>
          <span className="when">{when(e.occurredAt)}</span>
        </div>
        {changes.length > 0 && (
          <div className="tl-meta" style={{ display: "grid", gap: "var(--s-2)" }}>
            {changes.map(([field, [before, after]]) => (
              <span key={field}>
                {label(field)}: <span className="mono">{show(before)}</span> →{" "}
                <span className="mono">{show(after)}</span>
              </span>
            ))}
          </div>
        )}
      </div>
    </li>
  );
}

export function History({ entity, id }: { entity: string; id: string }) {
  const audit = useAudit(entity, id, true);
  const entries = audit.data;
  return (
    <div className="card">
      <div className="card-head">
        <h3>Field history</h3>
      </div>
      {audit.isError ? (
        <ErrorState error={audit.error} onRetry={() => void audit.refetch()} />
      ) : audit.isPending ? (
        <div className="rel-empty">Loading history…</div>
      ) : !entries?.length ? (
        <EmptyState icon="clock" title="No changes recorded" />
      ) : (
        <ul className="tl">
          {entries.map((e) => (
            <Entry key={e.id} e={e} />
          ))}
        </ul>
      )}
    </div>
  );
}
